// modules/facturacion/components/PrefacturaServicioModal.jsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, MenuItem, Alert, CircularProgress,
  Box, Divider, Grid, Typography, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Checkbox, IconButton, Paper, Collapse, Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon    from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PriceChangeIcon from "@mui/icons-material/PriceChange";
import axios from "../../../config/axiosConfig";
import InformacionFacturacionDialog from "../../clientes/components/InformacionFacturacionDialog";
import ServicioEditDialog from "../../clientes/components/ServicioEditDialog";
import { getServicioById } from "../../clientes/services/clienteService";
import { fieldSx } from "../../egresos/components/formStyles";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const mesNumeroANombre = (numero) => {
  const n = parseInt(numero, 10);
  return n >= 1 && n <= 12 ? MESES[n - 1] : "";
};

const SUCURSALES = ["La Paz", "Cochabamba", "Santa Cruz"];
const MONEDAS    = [{ value: 1, label: "Dólares" }, { value: 2, label: "Bolivianos" }];

const formatJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value || "");
  }
};

const TIPOS_DOCUMENTO = [
  { codigo: 1, nombre: "CI - CÉDULA DE IDENTIDAD" },
  { codigo: 2, nombre: "CEX - CÉDULA DE IDENTIDAD DE EXTRANJERO" },
  { codigo: 3, nombre: "PAS - PASAPORTE" },
  { codigo: 4, nombre: "OD - OTRO DOCUMENTO DE IDENTIDAD" },
  { codigo: 5, nombre: "NIT - NÚMERO DE IDENTIFICACIÓN TRIBUTARIA" },
];

const mesActual  = new Date().getMonth();
const yearActual = new Date().getFullYear();
const mesPrevio  = mesActual === 0 ? 11 : mesActual - 1;
const yearPrevio = mesActual === 0 ? yearActual - 1 : yearActual;

// Celda de encabezado súper compacta
const Th = styled(TableCell)({
  fontWeight: 600,
  backgroundColor: "#008080",
  color: "white",
  padding: "3px 4px",
  fontSize: "0.74rem",
  whiteSpace: "nowrap",
});

// Celda de datos al mínimo espacio posible
const Td = styled(TableCell)({
  padding: "0px 2px",
  verticalAlign: "middle",
  height: "28px",
});

// Estilos de ultra-compactación para controles del formulario superior e interno
const compactFieldSx = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderRadius: 1,
  },
  "& .MuiInputBase-root": {
    minHeight: 24,
    height: 24,
    fontSize: "0.74rem",
    borderRadius: 1,
  },
  "& .MuiInputBase-input": {
    py: 0.2,
    px: 0.5,
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.72rem",
    transform: "translate(8px, 4px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(10px, -7px) scale(0.75)",
    }
  },
  "& .MuiSelect-select": {
    py: "2px",
    lineHeight: "20px",
  },
};

const getLegalIdValue = (value) =>
  String(value || "").replace(/^(servicio|cliente)-/, "");

const cuotaActiva = (d) => (d.mostrar_cuota || (d.con_cuota ? "SI" : "NO")) === "SI";
const stripCuotaDetalle = (detalle = "") =>
  String(detalle).replace(/\s*\.?\s*Cuota No:\s*[^.]+$/i, "").trim();
const detalleConCuota = (d, mesFallback, yearFallback) => {
  const detalle = stripCuotaDetalle(d.detalle);
  if (!detalle || !cuotaActiva(d)) return detalle;
  const mesServicio = d.mes_servicio || mesFallback;
  const numeroMes = MESES.indexOf(mesServicio) + 1;
  const mes = numeroMes > 0 ? numeroMes : mesServicio;
  const year = d.year || yearFallback;
  return year ? `${detalle}. Cuota No: ${mes}/${year}` : detalle;
};

const buildDetalleFiscalPreviewBase = (detalleHippo, descripcionInterna, descripcionInternaBase) => {
  const detalleBase = stripCuotaDetalle(detalleHippo);
  const descripcion = String(descripcionInterna || "").trim();
  const baseInterna = String(descripcionInternaBase || "").trim();
  if (!descripcion || descripcion.localeCompare(baseInterna, "es", { sensitivity: "base" }) === 0) {
    return detalleBase;
  }

  let adicional = descripcion;
  if (baseInterna && descripcion.toLocaleLowerCase("es").startsWith(baseInterna.toLocaleLowerCase("es"))) {
    adicional = descripcion.slice(baseInterna.length);
  }
  adicional = adicional.replace(/^[\s:;,.|\-–—]+/, "").trim();
  if (!adicional || detalleBase.toLocaleLowerCase("es").includes(adicional.toLocaleLowerCase("es"))) {
    return detalleBase;
  }
  return `${detalleBase}. ${adicional}`;
};

const buildDetalleFiscalPreview = (detalleHippo, descripcionInterna, descripcionInternaBase) =>
  descripcionInterna === undefined
    ? buildDetalleFiscalPreviewBase(detalleHippo, descripcionInterna, descripcionInternaBase)
    : String(descripcionInterna || "").trim();

const marcarLineaPrincipal = (lineas = []) => {
  const principales = new Map();
  lineas.forEach((linea, index) => {
    if (!linea.group_key) return;
    if (Number(linea.orden_regla) === 1 && !principales.has(linea.group_key)) {
      principales.set(linea.group_key, index);
    }
  });
  return lineas.map((linea, index) => ({
    ...linea,
    aplicar_adicional: !linea.group_key || principales.get(linea.group_key) === index,
  }));
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const money = (value) => Number(value || 0).toFixed(2);
const round2 = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;
const parseDecimal = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const softPanelSx = {
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "#fafafa",
};

const warningPanelSx = {
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.action.hover
      : "#fff7ed",
  borderColor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.warning.dark
      : "#fed7aa",
};

const warningHeadCellSx = {
  py: 0.4,
  px: 0.6,
  fontSize: "0.72rem",
  fontWeight: 800,
  color: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.warning.light
      : "#7c2d12",
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.action.selected
      : "#ffedd5",
};
const allowDecimalInput = (value) => /^-?\d*([.,]\d*)?$/.test(String(value || ""));

const buildInternalGroups = (detalles = []) => {
  const map = new Map();
  detalles.forEach((d) => {
    if (!d.group_key) return;
    if (!map.has(d.group_key)) {
      map.set(d.group_key, {
        group_key: d.group_key,
        metodo_calculo: d.metodo_calculo || "",
        descripcion_interna: d.descripcion_interna || d.detalle || "",
        cantidad: d.cantidad ?? 0,
        precio_unitario_base: d.precio_unitario_base ?? d.precio_unitario ?? 0,
        descuento_interno: 0,
        descuento_interno_input: d.descuento_interno_input,
        mes_servicio: d.mes_servicio,
        year: d.year,
        mostrar_cuota: d.mostrar_cuota || "SI",
        con_cuota: (d.mostrar_cuota || "SI") !== "NO",
        lineas: [],
      });
    }
    const grupo = map.get(d.group_key);
    grupo.descuento_interno += parseDecimal(d.descuento_interno);
    grupo.lineas.push(d);
  });
  return Array.from(map.values()).map((g) => ({
    ...g,
    descuento_interno: g.descuento_interno_input ?? round2(g.descuento_interno),
  }));
};

const isDiscountPerUnitValid = (descuento, cantidad) => {
  const unitario = Number(descuento) / Number(cantidad || 1);
  return Math.abs((unitario * 100) - Math.round(unitario * 100)) < 1e-8;
};

const getValidGroupDiscountSuggestions = (descuentoBase, cantidad, lineas) => {
  const centro = Math.round(parseDecimal(descuentoBase) * 100);
  const encontrados = [];
  const vistos = new Set();
  const isValid = (valor) => lineas.every((linea) => {
    const pct = Number(linea.porcentaje_regla || 100) / 100;
    return isDiscountPerUnitValid(round2(valor * pct), cantidad);
  });

  for (let offset = 0; offset <= 2000 && encontrados.length < 4; offset += 1) {
    const opciones = offset === 0 ? [centro] : [centro - offset, centro + offset];
    for (const cents of opciones) {
      if (cents < 0 || vistos.has(cents)) continue;
      vistos.add(cents);
      const valor = cents / 100;
      if (isValid(valor)) {
        encontrados.push(valor);
        if (encontrados.length >= 4) break;
      }
    }
  }
  return encontrados.sort((a, b) => Math.abs(a - descuentoBase) - Math.abs(b - descuentoBase)).slice(0, 2);
};

const validarConceptosInternos = (detalles = []) => {
  for (const grupo of buildInternalGroups(detalles)) {
    const cantidad = parseDecimal(grupo.cantidad);
    const descuento = parseDecimal(grupo.descuento_interno);
    if (descuento <= 0) continue;

    const valido = grupo.lineas.every((linea) => {
      const pct = Number(linea.porcentaje_regla || 100) / 100;
      return isDiscountPerUnitValid(round2(descuento * pct), cantidad);
    });
    if (!valido) {
      const sugerencias = getValidGroupDiscountSuggestions(descuento, cantidad, grupo.lineas);
      const texto = sugerencias.length
        ? ` Valores aproximados validos: ${sugerencias.map((v) => v.toFixed(2)).join(" o ")}`
        : "";
      return `DESCUENTO INTERNO ${descuento.toFixed(2)} NO ES VALIDO.${texto}`;
    }
  }
  return null;
};

// ── Validaciones (replica validador.php) ─────────────────────
const validarDetalles = (detalles, nit, fac_Nombre) => {
  if (!nit || nit.toString().includes("\t"))
    return "RETIRE LAS TABULACIONES DEL NIT";
  if (!String(nit).match(/^[0-9]+$/))
    return "EL NÚMERO DE NIT O CÉDULA ES INVÁLIDO";
  if (String(nit).includes(" "))
    return "EL NÚMERO DE NIT NO DEBE TENER ESPACIOS";
  if (fac_Nombre && fac_Nombre.includes("\t"))
    return "RETIRE LAS TABULACIONES DEL NOMBRE";

  for (const [i, d] of detalles.entries()) {
    const n = i + 1;
    const cant     = parseDecimal(d.cantidad);
    const pu       = parseDecimal(d.precio_unitario);
    const desc     = parseDecimal(d.descuento_interno);
    const tipo     = Number(d.tipo);

    if (!d.id_fac_item || !Number.isFinite(tipo) || tipo <= 0)
      return `Linea ${n}: El item es obligatorio`;

    if (d.detalle?.includes("\t"))
      return `Línea ${n}: RETIRE LAS TABULACIONES DE LA DESCRIPCIÓN`;
    if (!Number.isFinite(cant) || cant === 0)
      return `Línea ${n}: La cantidad no debe ser 0`;
    if (!Number.isFinite(pu) || pu === 0)
      return `Línea ${n}: El precio unitario no debe ser 0`;
    if (!Number.isFinite(desc) || desc < 0)
      return `Línea ${n}: El descuento no debe ser negativo`;

    const subtotal = (cant * pu) - desc;

    if (subtotal === 0)
      return `Línea ${n}: El subtotal no debe ser 0`;

    if (tipo === 5 && subtotal > 0)
      return `Línea ${n}: El monto del descuento debe resultar en subtotal negativo`;

    if (desc > 0 && tipo !== 5) {
      if (desc > cant * pu)
        return `Línea ${n}: El descuento no debe ser mayor al subtotal`;
      
      const descPorUnidad = Number((desc / cant).toFixed(10));
      const decimales = (descPorUnidad.toString().split(".")[1] || "").length;
      if (decimales > 2) {
        const valAprox = Math.round(descPorUnidad * 100) / 100 * cant;
        return `Línea ${n}: DESCUENTO ${desc} NO ES VÁLIDO. Un valor aproximado válido es ${valAprox}`;
      }
    }
  }
  return null;
};

// ── Fila de detalle editable (Ultra-compacta) ─────────────────
const DetalleRow = ({
  d,
  idx,
  facItems,
  onChange,
  onDelete,
  disabled,
  descriptionDisabled = disabled,
  periodDisabled = disabled,
}) => {
  const cant     = parseDecimal(d.cantidad);
  const pu       = parseDecimal(d.precio_unitario);
  const desc     = parseDecimal(d.descuento_interno);
  const subtotal = (cant * pu) - desc;

  const handleChange = (field, value) => onChange(idx, field, value);

  const handleTipo = (id_fac_item) => {
    const item = facItems.find((f) => f.id === Number(id_fac_item));
    onChange(idx, "id_fac_item", id_fac_item);
    onChange(idx, "tipo", String(id_fac_item));
    if (item) onChange(idx, "detalle", item.item);
  };

  return (
    <TableRow hover sx={{ bgcolor: subtotal < 0 ? "rgba(211, 47, 47, 0.04)" : "inherit", "& .MuiTableCell-root": { py: 0.1 } }}>
      {/* Cantidad */}
      <Td>
        <TextField size="small" type="text" sx={fieldSx}
          inputProps={{ inputMode: "decimal", style: { width: 40, padding: "2px 4px", fontSize: "0.74rem" } }}
          value={d.cantidad} disabled={disabled}
          onChange={(e) => handleChange("cantidad", e.target.value)} />
      </Td>

      {/* Tipo / Código SIN */}
      <Td>
        <TextField select size="small"
          SelectProps={{ style: { fontSize: "0.74rem" } }}
          sx={{ ...fieldSx, width: 112 }}
          value={d.id_fac_item || ""} disabled={disabled}
          required
          error={!disabled && !d.id_fac_item}
          onChange={(e) => handleTipo(e.target.value)}>
          <MenuItem value="" sx={{ fontSize: "0.74rem" }}><em>Seleccione...</em></MenuItem>
          {facItems.map((f) => (
            <MenuItem key={f.id} value={f.id} sx={{ fontSize: "0.74rem" }}>{f.item}</MenuItem>
          ))}
        </TextField>
      </Td>

      {/* Descripción comercial */}
      <Td>
        <TextField size="small" fullWidth sx={fieldSx}
          inputProps={{ style: { padding: "2px 4px", fontSize: "0.74rem" } }}
          value={d.detalle || ""} disabled={descriptionDisabled}
          onChange={(e) => handleChange("detalle", e.target.value)} />
      </Td>

      {/* Periodo de Cuota */}
      <Td>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Checkbox size="small" sx={{ p: 0, ml: -0.5, mr: -0.25, transform: "scale(0.85)" }}
            checked={cuotaActiva(d)}
            onChange={(e) => {
              handleChange("con_cuota", e.target.checked);
              handleChange("mostrar_cuota", e.target.checked ? "SI" : "NO");
            }}
            disabled={periodDisabled}
          />
          <TextField select size="small"
            sx={{ ...fieldSx, width: 85 }}
            value={d.mes_servicio || MESES[mesPrevio]}
            onChange={(e) => handleChange("mes_servicio", e.target.value)}
            disabled={periodDisabled}>
            {MESES.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: "0.74rem" }}>{m}</MenuItem>)}
          </TextField>
          <TextField size="small" type="text" sx={fieldSx}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", style: { width: 42, padding: "2px 4px", fontSize: "0.74rem" } }}
            value={d.year || yearPrevio}
            onChange={(e) => handleChange("year", e.target.value)}
            disabled={periodDisabled}
          />
        </Box>
      </Td>

      {/* Precio Unitario */}
      <Td>
        <TextField size="small" type="text" sx={fieldSx}
          inputProps={{ inputMode: "decimal", style: { width: 50, padding: "2px 4px", fontSize: "0.74rem" } }}
          value={d.precio_unitario} disabled={disabled}
          onChange={(e) => { if (allowDecimalInput(e.target.value)) handleChange("precio_unitario", e.target.value); }} />
      </Td>

      {/* Descuento */}
      <Td>
        <TextField size="small" type="text" sx={fieldSx}
          inputProps={{ inputMode: "decimal", style: { width: 48, padding: "2px 4px", fontSize: "0.74rem" } }}
          value={d.descuento_interno ?? ""} disabled={disabled}
          onChange={(e) => handleChange("descuento_interno", e.target.value)} />
      </Td>

      {/* Subtotal Línea */}
      <Td align="right">
        <Typography variant="body2" fontWeight={600}
          color={subtotal < 0 ? "error" : "inherit"} sx={{ fontSize: "0.74rem", pr: 0.5 }}>
          {subtotal.toFixed(2)}
        </Typography>
      </Td>

      {/* Eliminar Fila */}
      <Td align="center">
        <IconButton size="small" color="error" onClick={() => onDelete(idx)} disabled={disabled} sx={{ p: 0.25 }}>
          <DeleteIcon style={{ fontSize: "0.9rem" }} />
        </IconButton>
      </Td>
    </TableRow>
  );
};

const ConceptoInternoRow = ({ grupo, conceptos, onChange, onTipoChange, onDelete, disabled }) => {
  const cant = parseDecimal(grupo.cantidad);
  const base = parseDecimal(grupo.precio_unitario_base);
  const desc = parseDecimal(grupo.descuento_interno);
  const total = (cant * base) - desc;

  return (
    <TableRow hover sx={{ "& .MuiTableCell-root": { py: 0.15 } }}>
      <Td align="center">
        <TextField size="small" type="text" sx={fieldSx}
          inputProps={{ inputMode: "decimal", style: { width: 50, padding: "2px 4px", fontSize: "0.74rem", textAlign: "center" } }}
          value={grupo.cantidad}
          disabled={disabled}
          onChange={(e) => { if (allowDecimalInput(e.target.value)) onChange(grupo.group_key, "cantidad", e.target.value); }}
        />
      </Td>
      <Td>
        <TextField
          select
          size="small"
          fullWidth
          sx={fieldSx}
          value={grupo.metodo_calculo || ""}
          disabled={disabled}
          onChange={(e) => {
            const metodoCalculo = e.target.value;
            const concepto = conceptos.find(
              (item) => String(item.metodo_calculo) === String(metodoCalculo)
            );
            onTipoChange(grupo.group_key, {
              ...grupo,
              metodo_calculo: metodoCalculo,
              descripcion_interna: concepto?.descripcion_interna || grupo.descripcion_interna,
            });
          }}
        >
          <MenuItem value=""><em>Seleccione...</em></MenuItem>
          {conceptos.map((concepto) => (
            <MenuItem key={concepto.metodo_calculo} value={concepto.metodo_calculo}>
              {concepto.metodo_calculo}
            </MenuItem>
          ))}
        </TextField>
      </Td>
      <Td>
        <TextField size="small" fullWidth sx={fieldSx}
          value={grupo.descripcion_interna || ""}
          disabled={disabled}
          onChange={(e) => onChange(grupo.group_key, "descripcion_interna", e.target.value)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.15, pl: 0.5 }}>
          {grupo.lineas.map((l) => `${l.porcentaje_regla || 100}% ${l.detalle}`).join(" | ")}
        </Typography>
      </Td>
      <Td align="right">
        <TextField size="small" type="text" sx={fieldSx}
          inputProps={{ inputMode: "decimal", style: { width: 70, padding: "2px 4px", fontSize: "0.74rem", textAlign: "left" } }}
          value={grupo.precio_unitario_base}
          disabled={disabled}
          onChange={(e) => { if (allowDecimalInput(e.target.value)) onChange(grupo.group_key, "precio_unitario_base", e.target.value); }}
        />
      </Td>
      <Td align="right">
        <TextField size="small" type="text" sx={fieldSx}
          inputProps={{ inputMode: "decimal", style: { width: 70, padding: "2px 4px", fontSize: "0.74rem", textAlign: "left" } }}
          value={grupo.descuento_interno}
          disabled={disabled}
          onChange={(e) => onChange(grupo.group_key, "descuento_interno", e.target.value)}
        />
      </Td>
      <Td>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Checkbox size="small" sx={{ p: 0, transform: "scale(0.85)" }}
            checked={(grupo.mostrar_cuota || "SI") !== "NO"}
            disabled={disabled}
            onChange={(e) => onChange(grupo.group_key, "mostrar_cuota", e.target.checked ? "SI" : "NO")}
          />
          <TextField select size="small" sx={{ ...fieldSx, width: 85 }}
            value={grupo.mes_servicio || MESES[mesPrevio]}
            disabled={disabled}
            onChange={(e) => onChange(grupo.group_key, "mes_servicio", e.target.value)}
          >
            {MESES.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: "0.74rem" }}>{m}</MenuItem>)}
          </TextField>
          <TextField size="small" type="text" sx={fieldSx}
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*", style: { width: 42, padding: "2px 4px", fontSize: "0.74rem" } }}
            value={grupo.year || yearPrevio}
            disabled={disabled}
            onChange={(e) => onChange(grupo.group_key, "year", e.target.value)}
          />
        </Box>
      </Td>
      <Td align="right">
        <Typography variant="body2" fontWeight={800} sx={{ fontSize: "0.76rem", pr: 0.5 }}>
          {total.toFixed(2)}
        </Typography>
      </Td>
      <Td align="center">
        <Tooltip title="Eliminar concepto interno">
          <IconButton size="small" color="error" onClick={() => onDelete(grupo.group_key)} disabled={disabled} sx={{ p: 0.2 }}>
            <DeleteIcon style={{ fontSize: "0.9rem" }} />
          </IconButton>
        </Tooltip>
      </Td>
    </TableRow>
  );
};


// Aviso previo a emitir: equipos retirados de este servicio desde su última factura (neto de los repuestos).
const AvisoRetiros = ({ retiros, sx }) => {
  if (!retiros) return null;
  const tipos = [["modems", "módem", "módems"], ["radio", "radio", "radios"], ["bateria", "batería", "baterías"], ["varios", "equipo vario", "equipos varios"], ["chip", "chip", "chips"]]
    .map(([k, u, m]) => {
      const t = retiros[k] || {};
      const detalle = t.repuestos > 0 ? ` (${t.retirados.length} retirados, ${t.repuestos} repuestos)` : "";
      return { n: t.neto || 0, txt: `${t.neto} ${t.neto === 1 ? u : m}${detalle}` };
    })
    .filter((t) => t.n > 0);
  if (!tipos.length) return null;
  // Una placa puede tener 2 módems: se indica cuántos se retiraron de cada una
  const porPlaca = {};
  (retiros.modems?.retirados || []).forEach((m) => { const k = m.placa || "sin placa"; porPlaca[k] = (porPlaca[k] || 0) + 1; });
  const placas = Object.entries(porPlaca).map(([pl, n]) => (n > 1 ? `${pl} (${n} módems)` : pl));
  const placasTxt = placas.length ? ` Placas: ${placas.slice(0, 5).join(", ")}${placas.length > 5 ? ` y ${placas.length - 5} más` : ""}.` : "";
  const fechas = ["modems", "radio", "bateria", "varios", "chip"].map((k) => retiros[k]?.retirados?.[0]?.fecha).filter(Boolean).sort().reverse();
  const ultima = fechas[0] ? ` Último retiro: ${formatDate(fechas[0])}.` : "";
  const desde = retiros.desde ? formatDate(retiros.desde) : "";
  const lapso = retiros.motivo === "ultima_factura" ? `desde la última factura (${desde})` : `en el último mes (desde ${desde})`;
  return (
    <Alert severity="warning" sx={{ py: 0, px: 1, fontSize: "0.76rem", ...sx }}>
      <strong>Se retiraron equipos de este servicio {lapso}:</strong> {tipos.map((t) => t.txt).join(", ")}.{placasTxt}{ultima} Revisa el cobro antes de emitir.
    </Alert>
  );
};

// Bloque con título: barra de encabezado (título + acciones a la derecha) y cuerpo. Ordena el formulario por secciones.
const Seccion = ({ titulo, nota, acciones, color = "#008080", children, sinCuerpo = false }) => (
  <Box sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper", mb: 0.75, overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, flexWrap: "wrap", px: 1, py: 0.25, minHeight: 30,
      bgcolor: "action.hover", borderBottom: children && !sinCuerpo ? 1 : 0, borderColor: "divider", borderLeft: `3px solid ${color}` }}>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, flexWrap: "wrap" }}>
        <Typography variant="caption" sx={{ fontWeight: 800, fontSize: "0.72rem", letterSpacing: 0.4, color }}>{titulo}</Typography>
        {nota && <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>{nota}</Typography>}
      </Box>
      {acciones && <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", flexWrap: "wrap" }}>{acciones}</Box>}
    </Box>
    {children && (sinCuerpo ? children : <Box sx={{ p: 0.75 }}>{children}</Box>)}
  </Box>
);

const HistorialFacturasPanel = ({ historial, compact = false, onCopiarDetalle }) => (
  !compact && !historial.facturas.length && !historial.ultimaFacturaDetalle?.detalles?.length ? (
    <Paper variant="outlined" sx={{ mb: 0.75, px: 1, py: 0.5, bgcolor: "background.paper" }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
        <strong style={{ color: "#008080" }}>Historial:</strong> sin facturas anteriores para este servicio.
      </Typography>
    </Paper>
  ) : (
  <Paper variant="outlined" sx={{ mb: 0.75, overflow: "hidden", bgcolor: "background.paper" }}>
    <Grid container spacing={1} sx={{ p: 1 }}>
      {!compact && (
        <Grid item size={{ xs: 12, md: 8 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5, fontSize: "0.82rem", color: "#008080" }}>
            Últimas 3 facturas
          </Typography>
          <TableContainer>
            <Table size="small" sx={{ "& td": { py: 0.25, px: 0.6, fontSize: "0.72rem" } }}>
              <TableHead>
                <TableRow>
                  <Th>Número de Factura</Th>
                  <Th>Fecha de Factura</Th>
                  <Th>Fecha de Emisión</Th>
                  <Th>Tipo</Th>
                  <Th>Emitido</Th>
                  <Th>Mes-Año de Servicio</Th>
                  <Th align="right">Bolivianos</Th>
                  <Th align="right">Dólares</Th>
                  <Th align="right">Saldo</Th>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.facturas.length === 0 ? (
                  <TableRow>
                    <Td colSpan={9}>
                      <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>Sin facturas anteriores.</Typography>
                    </Td>
                  </TableRow>
                ) : historial.facturas.map((f) => (
                  <TableRow key={f.cod}>
                    <Td>{f.num_fac || f.coddos || 0}</Td>
                    <Td>{formatDate(f.fecha_fac || f.fecha_emi)}</Td>
                    <Td>{formatDate(f.fecha_emi || f.fecha_fac)}</Td>
                    <Td>{f.venta || "-"}</Td>
                    <Td>{f.fac_ind === "si" ? "Individual" : "Masiva"}</Td>
                    <Td>{f.mes} {f.year}</Td>
                    <Td align="right">{money(f.monto_total)}</Td>
                    <Td align="right">{money(f.dolares)}</Td>
                    <Td align="right">{money(f.saldo)}</Td>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      <Grid item size={{ xs: 12, md: compact ? 12 : 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, gap: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, fontSize: "0.82rem", color: "#008080" }}>
            Detalle de Última Factura
          </Typography>
          {onCopiarDetalle && !!historial.ultimaFacturaDetalle?.detalles?.length && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
              onClick={onCopiarDetalle}
              sx={{ height: 22, fontSize: "0.65rem", py: 0, px: 1 }}
            >
              Copiar detalle
            </Button>
          )}
        </Box>
        <Box sx={{ border: "1px solid", borderColor: "divider", mb: 0.5, ...softPanelSx }}>
          <Grid container>
            <Grid item xs={8} sx={{ px: 0.75, py: 0.4, borderRight: "1px solid #d6dde3" }}>
              <Typography variant="caption" sx={{ fontSize: "0.7rem" }}><strong>Nombre:</strong> {historial.ultimaFacturaDetalle?.nombre || "-"}</Typography>
            </Grid>
            <Grid item xs={4} sx={{ px: 0.75, py: 0.4 }}>
              <Typography variant="caption" sx={{ fontSize: "0.7rem" }}><strong>NIT:</strong> {historial.ultimaFacturaDetalle?.nit || "-"}</Typography>
            </Grid>
          </Grid>
        </Box>
        <TableContainer>
          <Table size="small" sx={{ "& td": { py: 0.25, px: 0.6, fontSize: "0.72rem" } }}>
            <TableHead>
              <TableRow>
                <Th>Cantidad</Th>
                <Th>Detalle</Th>
                <Th align="right">Precio Unitario</Th>
                <Th align="right">Total</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {!historial.ultimaFacturaDetalle?.detalles?.length ? (
                <TableRow>
                  <Td colSpan={4}>
                    <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>Sin detalle disponible.</Typography>
                  </Td>
                </TableRow>
              ) : historial.ultimaFacturaDetalle.detalles.map((d, idx) => (
                <TableRow key={String(d.detalle || "") + "-" + idx}>
                  <Td>{d.cantidad}</Td>
                  <Td>{d.detalle}</Td>
                  <Td align="right">{money(d.precio_unitario)}</Td>
                  <Td align="right">{money(d.total)}</Td>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  </Paper>
  )
);

// ── Modal principal ───────────────────────────────────────────
const PrefacturaServicioModal = ({ open, onClose, onSuccess, servicio, tipo, progreso, individual = false }) => {
  const [loading,     setLoading]     = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error,       setError]       = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [errorDetailsOpen, setErrorDetailsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [retiros, setRetiros] = useState(null);
  const [facItems,    setFacItems]    = useState([]);
  const [infoLegales, setInfoLegales] = useState([]);
  const [historialFacturacion, setHistorialFacturacion] = useState({ facturas: [], ultimaFacturaDetalle: null });
  const [facturacionDialogOpen, setFacturacionDialogOpen] = useState(false);
  // Solo se llena cuando se abre desde "Registrar para este servicio" (aviso de falta de
  // registro) — precarga el diálogo con lo que ya se estaba mostrando por defecto, en vez
  // de un formulario vacío. El botón original ("Registrar datos de facturación") lo deja
  // en null para que siga abriendo en blanco, como antes.
  const [facturacionDialogInitialData, setFacturacionDialogInitialData] = useState(null);
  const [codClienteResuelto, setCodClienteResuelto] = useState(null);
  // Empieza en true para no mostrar la alerta de "falta registrar" mientras todavía no
  // cargó nada (se corrige apenas responde /api/facturas/servicio-detalle).
  const [tieneLegalServicio, setTieneLegalServicio] = useState(true);
  // "Editar Precio del Servicio" — reusa el mismo ServicioEditDialog de /clientes
  // (mismo endpoint, misma validación) en vez de duplicar un formulario de precios
  // acá. Necesita el cliente_servicio COMPLETO (pu_1..pu_4, estado, etc.), que el
  // `servicio` que llega como prop no trae (viene de la búsqueda/listado, con
  // columnas acotadas) — por eso se pide aparte con getServicioById recién al abrir.
  const [editarPrecioOpen, setEditarPrecioOpen] = useState(false);
  const [servicioParaEditarPrecio, setServicioParaEditarPrecio] = useState(null);
  const [cargandoServicioPrecio, setCargandoServicioPrecio] = useState(false);

  const cargarInfoLegales = useCallback(async (codCliente) => {
    if (!codCliente || !servicio?.cod_servicio_cliente) return;
    try {
      const resLegal = await axios.get("/api/facturas/info-legal", {
        params: {
          cod_cliente: codCliente,
          cod_servicio_cliente: servicio.cod_servicio_cliente,
        },
      });
      const rawLegal = resLegal.data;
      const legales = Array.isArray(rawLegal) ? rawLegal
                    : Array.isArray(rawLegal?.data) ? rawLegal.data
                    : [];
      setInfoLegales(legales);
    } catch {
      // silencioso: si falla, el usuario simplemente no ve el historial de razon social/nit
    }
  }, [servicio?.cod_servicio_cliente]);

  const esAdelantado = servicio?.pago === "adelantado";
  const mesDefaultIdx = esAdelantado ? mesActual : mesPrevio;
  const yearDefault   = esAdelantado ? yearActual : yearPrevio;

  const [form, setForm] = useState({
    id_legal                    : "",
    nit                         : "",
    fac_Nombre                  : "",
    codigoTipoDocumentoIdentidad: 5,
    mes                         : String(mesDefaultIdx + 1),
    year                        : yearDefault,
    sucursal                    : servicio?.sucursal_facturar || "La Paz",
    codigoMoneda                : servicio?.moneda || 2,
    venta                       : "Alquiler",
  });
  const formMesNombre = mesNumeroANombre(form.mes) || form.mes;

  const [detalles, setDetalles] = useState([]);
  const [reglasDetalle, setReglasDetalle] = useState([]);
  const [agregandoConcepto, setAgregandoConcepto] = useState(false);
  const [conceptoDraft, setConceptoDraft] = useState({
    metodo_calculo: "",
    descripcion_interna: "",
    cantidad: "1",
    precio_unitario_base: "0",
    descuento_interno: "0",
    mostrar_cuota: "SI",
    mes_servicio: "",
    year: "",
  });

  const cargarDataModal = useCallback(async () => {
    if (!servicio) return;
      try {
        const [resDetalle, resItems, resHistorial, resReglas] = await Promise.all([
          axios.get("/api/facturas/servicio-detalle", {
            params: { cod_servicio_cliente: servicio.cod_servicio_cliente },
          }),
          axios.get("/api/facturas/fac-items", {
            params: { cod_servicio: servicio.cod_servicio },
          }),
          axios.get(`/api/facturas/reporte/${servicio.cod_servicio_cliente}`, { params: { excluir_anuladas: 1 } }),
          axios.get("/api/facturas/reglas-detalle", {
            params: { cod_servicio: servicio.cod_servicio },
          }),
        ]);

        const rawDetalle = resDetalle.data;
        const data = rawDetalle?.data ?? rawDetalle;
        setTieneLegalServicio(!!data?.tiene_informacion_legal_servicio);

        const rawItems = resItems.data;
        const items = Array.isArray(rawItems) ? rawItems
                    : Array.isArray(rawItems?.data) ? rawItems.data
                    : [];
        setFacItems(items);

        const rawHistorial = resHistorial.data?.data ?? resHistorial.data;
        setHistorialFacturacion({
          facturas: Array.isArray(rawHistorial?.facturas) ? rawHistorial.facturas.slice(0, 3) : [],
          ultimaFacturaDetalle: rawHistorial?.ultimaFacturaDetalle || null,
        });
        const rawReglas = resReglas.data?.data ?? resReglas.data;
        setReglasDetalle(Array.isArray(rawReglas) ? rawReglas : []);
        const tieneReglasInternas = Array.isArray(rawReglas) &&
          rawReglas.some((concepto) => Array.isArray(concepto?.reglas) && concepto.reglas.length > 0);
        setAgregandoConcepto(false);
        setConceptoDraft({
          metodo_calculo: "",
          descripcion_interna: "",
          cantidad: "1",
          precio_unitario_base: "0",
          descuento_interno: "0",
          mostrar_cuota: "SI",
          mes_servicio: MESES[mesDefaultIdx],
          year: String(yearDefault),
        });

        // Prioridad NIT/Nombre/Tipo Documento: la última factura/pre-factura emitida
        // primero (es lo que realmente se usó la vez pasada), informacion_legal /
        // cliente_servicio_informacion_legal (servicio-detalle) como respaldo si no hay
        // facturas previas o la última no trae NIT.
        const ultimaFactura = rawHistorial?.ultimaFacturaDetalle || null;
        if (ultimaFactura?.nit || data?.nit) {
          setForm((p) => ({
            ...p,
            nit         : ultimaFactura?.nit || data?.nit || "",
            fac_Nombre  : ultimaFactura?.nombre || data?.nombre_facturar || "",
            id_legal    : data?.id_legal ? `servicio-${data.id_legal}` : "",
            sucursal    : data?.sucursal || servicio.sucursal_facturar || "La Paz",
            codigoMoneda: data?.moneda || servicio.moneda || 2,
            codigoTipoDocumentoIdentidad: ultimaFactura?.factura?.tipo_documento_identidad || data?.tipo_documento_identidad || 5
          }));
        }

        const codCliente = data?.cod_cliente || servicio.cod_cliente || null;
        setCodClienteResuelto(codCliente);
        if (codCliente) {
          await cargarInfoLegales(codCliente);
        }

        const lineas = data?.detalles?.length > 0
          ? data.detalles.map((d) => ({
              ...d,
              detalle_hippo_base: stripCuotaDetalle(d.detalle),
              descripcion_interna_base: d.descripcion_interna || stripCuotaDetalle(d.detalle),
              descuento_interno: d.descuento_interno || 0,
              mes_servicio     : d.mes_servicio || MESES[mesDefaultIdx],
              year             : String(d.year || yearDefault),
              con_cuota        : (d.mostrar_cuota !== "NO"),
              mostrar_cuota    : d.mostrar_cuota || "SI",
              p_3              : "",
            }))
          : tieneReglasInternas
            ? []
            : [{
              id_fac_item: "", tipo: "", detalle: servicio.descripcion || "",
              cantidad: 1, precio_unitario: Number(servicio.precio || 0), descuento_interno: 0,
              mes_servicio: MESES[mesDefaultIdx], year: String(yearDefault),
              con_cuota: true, mostrar_cuota: "SI", p_3: "",
            }];

        setDetalles(marcarLineaPrincipal(lineas));
      } catch (e) {
        setError(e?.response?.data?.message || "Error al cargar los datos de facturación");
        setDetalles([{
          id_fac_item: "", tipo: "", detalle: servicio.descripcion || "",
          cantidad: 1, precio_unitario: Number(servicio.precio || 0), descuento_interno: 0,
          mes_servicio: MESES[mesDefaultIdx], year: String(yearDefault),
          con_cuota: true, mostrar_cuota: "SI", p_3: "",
        }]);
        if (servicio.cod_cliente) {
          await cargarInfoLegales(servicio.cod_cliente);
        }
      } finally {
        setLoadingData(false);
      }
  }, [servicio, mesDefaultIdx, yearDefault, cargarInfoLegales]);

  useEffect(() => {
    setRetiros(null);
    if (!open || !servicio?.cod_servicio_cliente) return undefined;
    let activo = true;
    axios.get("/api/facturas/retiros-recientes", { params: { cod_servicio_cliente: servicio.cod_servicio_cliente } })
      .then((res) => { if (activo) setRetiros(res.data?.data ?? res.data); })
      .catch(() => {});
    return () => { activo = false; };
  }, [open, servicio?.cod_servicio_cliente]);

  useEffect(() => {
    if (!open || !servicio) return;
    setLoadingData(true);
    setError(null);
    setErrorDetails(null);
    setPreviewOpen(false);
    setErrorDetailsOpen(false);
    setHistorialFacturacion({ facturas: [], ultimaFacturaDetalle: null });
    setTieneLegalServicio(true);
    // Se resuelve de entrada desde el propio prop `servicio` (ya trae cod_cliente
    // desde la búsqueda) en vez de esperar la respuesta de /servicio-detalle — así
    // "Registrar datos de facturación" queda habilitado aunque esa consulta falle
    // (ej. "Servicio no encontrado o sin información legal" cuando el servicio no
    // tiene ningún dato legal registrado todavía, que es justo el caso que se
    // quiere poder resolver desde este botón).
    setCodClienteResuelto(servicio.cod_cliente || null);

    cargarDataModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [servicio, open]);

  // "Activar servicio" (ver alerta de servicio inactivo): activa cliente_servicio
  // y vuelve a cargar todo — mismo endpoint genérico que ya usa ServicioEditDialog
  // (PUT /clientes/:cod_cliente/servicios/:cod_servicio_cliente).
  const [activandoServicio, setActivandoServicio] = useState(false);
  const handleActivarServicio = async () => {
    if (!servicio?.cod_cliente || !servicio?.cod_servicio_cliente) return;
    if (!window.confirm(`Se activará el servicio de "${servicio.razon_social || servicio.cod_cliente}". ¿Continuar?`)) return;
    setActivandoServicio(true);
    try {
      await axios.put(
        `/api/clientes/${servicio.cod_cliente}/servicios/${servicio.cod_servicio_cliente}`,
        { estado: "Activo" }
      );
      setLoadingData(true);
      setError(null);
      await cargarDataModal();
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo activar el servicio.");
    } finally {
      setActivandoServicio(false);
    }
  };

  // El backend mezcla en un solo mensaje "no existe" y "servicio inactivo" (ver
  // facturaService.getServicioDetalle) — se ofrece "Activar servicio" ante ese
  // error genérico porque en la práctica esta pantalla solo llega a mostrarse
  // para servicios que sí existen (vienen de una búsqueda/lista real), así que
  // si tira ese error la causa casi siempre es que quedó Inactivo.
  const servicioPosiblementeInactivo = /no encontrado/i.test(error || "");

  const handleAbrirEditarPrecio = async () => {
    if (!servicio?.cod_cliente || !servicio?.cod_servicio_cliente) return;
    setCargandoServicioPrecio(true);
    try {
      const res = await getServicioById(servicio.cod_cliente, servicio.cod_servicio_cliente);
      const data = res.data?.data ?? res.data;
      setServicioParaEditarPrecio(data);
      setEditarPrecioOpen(true);
    } catch (e) {
      setError(e?.response?.data?.message || "No se pudo cargar el servicio para editar el precio.");
    } finally {
      setCargandoServicioPrecio(false);
    }
  };

  const handleForm = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSelectLegal = (id_legal) => {
    const legal = infoLegales.find((l) => String(l.legal_key || l.id_legal) === String(id_legal));
    if (legal) {
      setForm((p) => ({ 
        ...p, 
        id_legal: legal.legal_key || id_legal, 
        nit: legal.nit, 
        fac_Nombre: legal.nombre_facturar,
        codigoTipoDocumentoIdentidad: legal.tipo_documento_identidad || 5
      }));
    }
  };

  const handleDetalleChange = (idx, field, value) => {
    setDetalles((prev) => prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));
  };

  const handleConceptoInternoChange = (groupKey, field, value) => {
    setDetalles((prev) => {
      const grupoActual = buildInternalGroups(prev).find((g) => g.group_key === groupKey);
      const nextGroup = {
        ...grupoActual,
        [field]: value,
      };

      return prev.map((d) => {
        if (d.group_key !== groupKey) return d;
        const pct = Number(d.porcentaje_regla || 100) / 100;
        const cantidad = nextGroup.cantidad;
        const base = parseDecimal(nextGroup.precio_unitario_base);
        const descuentoBase = parseDecimal(nextGroup.descuento_interno);
        const mostrarCuota = nextGroup.mostrar_cuota || "SI";
        const descripcionInterna = nextGroup.descripcion_interna || "";

        return {
          ...d,
          descripcion_interna: descripcionInterna,
          detalle: d.aplicar_adicional === false
            ? stripCuotaDetalle(d.detalle_hippo_base || d.detalle)
            : buildDetalleFiscalPreview(
                d.detalle_hippo_base || d.detalle,
                descripcionInterna,
                d.descripcion_interna_base || ""
              ),
          cantidad,
          precio_unitario_base: nextGroup.precio_unitario_base,
          precio_unitario: round2(base * pct),
          descuento_interno: round2(descuentoBase * pct),
          descuento_interno_input: field === "descuento_interno" ? value : d.descuento_interno_input,
          mes_servicio: nextGroup.mes_servicio,
          year: nextGroup.year,
          mostrar_cuota: mostrarCuota,
          con_cuota: mostrarCuota !== "NO",
        };
      });
    });
  };

  const agregarDetalle = () => {
    setDetalles((prev) => [...prev, {
      id_fac_item: "", tipo: "", detalle: "", cantidad: 1,
      precio_unitario: 0, descuento_interno: 0,
      mes_servicio: formMesNombre, year: String(form.year),
      con_cuota: true, mostrar_cuota: "SI", p_3: "",
    }]);
  };

  // "Copiar detalle" (Detalle de Última Factura): reconstruye el Concepto Interno
  // agrupado a partir de la última factura. Desde que facturaModel.getDetalleServicioFacturas
  // también trae fd.id_regla_detalle/fd.descripcion_interna (antes solo cantidad/detalle/
  // precio/descuento), se empareja cada línea con la regla real por ese id — exacto, no
  // depende de que el texto de la descripción siga coincidiendo con detalle_hippo (se
  // rompía apenas la descripción interna llevaba texto propio, ej. series de equipo). Solo
  // facturas viejas sin id_regla_detalle guardado caen al matcheo por texto de antes.
  const reconstruirConceptoInterno = (lineas) => {
    const normalizar = (t) => stripCuotaDetalle(t || "").trim().toLocaleLowerCase("es");
    const todasConId = lineas.length > 0 && lineas.every((d) => d.id_regla_detalle != null);

    let concepto = null;
    let parejas = null;

    if (todasConId) {
      concepto = reglasDetalle.find((c) => {
        if (!c?.reglas?.length || c.reglas.length !== lineas.length) return false;
        const idsDisponibles = new Set(c.reglas.map((r) => r.id));
        return lineas.every((d) => idsDisponibles.has(d.id_regla_detalle));
      });
      if (concepto) {
        const reglasDisponibles = [...concepto.reglas];
        parejas = lineas.map((linea) => {
          const idx = reglasDisponibles.findIndex((r) => r.id === linea.id_regla_detalle);
          const regla = reglasDisponibles.splice(idx, 1)[0];
          return { linea, regla };
        });
      }
    }

    if (!concepto) {
      const textosFactura = lineas.map((d) => normalizar(d.detalle));
      concepto = reglasDetalle.find((c) => {
        if (!c?.reglas?.length || c.reglas.length !== lineas.length) return false;
        const disponibles = c.reglas.map((r) => normalizar(r.detalle_hippo));
        return textosFactura.every((t) => {
          const idx = disponibles.indexOf(t);
          if (idx === -1) return false;
          disponibles.splice(idx, 1);
          return true;
        });
      });
      if (!concepto) return null;

      const reglasDisponibles = [...concepto.reglas];
      parejas = lineas.map((linea) => {
        const texto = normalizar(linea.detalle);
        const idx = reglasDisponibles.findIndex((r) => normalizar(r.detalle_hippo) === texto);
        const regla = reglasDisponibles.splice(idx, 1)[0];
        return { linea, regla };
      });
    }

    const parejaPrincipal = parejas.find((p) => Number(p.regla.orden) === 1) || parejas[0];
    const porcentajePrincipal = Number(parejaPrincipal.regla.porcentaje || 0);
    if (!porcentajePrincipal) return null;

    const cantidad = parejaPrincipal.linea.cantidad ?? 1;
    const precioBase = round2(Number(parejaPrincipal.linea.precio_unitario || 0) / (porcentajePrincipal / 100));
    const descuentoBase = round2(Number(parejaPrincipal.linea.descuento_interno || 0) / (porcentajePrincipal / 100));
    const descripcionInterna = stripCuotaDetalle(
      parejaPrincipal.linea.descripcion_interna || parejaPrincipal.linea.detalle
      || parejaPrincipal.regla.descripcion_interna || parejaPrincipal.regla.detalle_hippo
    );

    const groupKey = `manual-${concepto.metodo_calculo}-${Date.now()}`;
    let adicionalAsignado = false;
    return concepto.reglas.map((regla) => {
      const aplicarAdicional = !adicionalAsignado && Number(regla.orden) === 1;
      if (aplicarAdicional) adicionalAsignado = true;
      return {
        id_fac_item: Number(regla.id_fac_item),
        id_regla_detalle: regla.id,
        tipo: String(regla.id_fac_item),
        detalle: aplicarAdicional
          ? buildDetalleFiscalPreview(regla.detalle_hippo, descripcionInterna, regla.descripcion_interna)
          : regla.detalle_hippo,
        detalle_hippo_base: regla.detalle_hippo,
        descripcion_interna_base: regla.descripcion_interna || regla.detalle_hippo,
        descripcion_interna: descripcionInterna,
        aplicar_adicional: aplicarAdicional,
        cantidad,
        precio_unitario_base: precioBase,
        precio_unitario: round2(parseDecimal(precioBase) * (Number(regla.porcentaje || 0) / 100)),
        porcentaje_regla: Number(regla.porcentaje || 0),
        orden_regla: Number(regla.orden || 0),
        descuento_interno: round2(parseDecimal(descuentoBase) * (Number(regla.porcentaje || 0) / 100)),
        descuento_interno_input: descuentoBase,
        mes_servicio: formMesNombre,
        year: String(form.year),
        con_cuota: true,
        mostrar_cuota: "SI",
        metodo_calculo: regla.metodo_calculo,
        group_key: groupKey,
        p_3: "",
      };
    });
  };

  // El botón que dispara esto solo se muestra cuando reconstruirConceptoInterno ya dio
  // resultado (ver puedeCopiarDetalleInterno), es decir, cuando la última factura sí
  // tenía detalle interno — así que acá siempre debería reconstruir.
  const handleCopiarDetalle = () => {
    const lineas = historialFacturacion.ultimaFacturaDetalle?.detalles || [];
    const reconstruido = reconstruirConceptoInterno(lineas);
    if (!reconstruido) return;
    if (detalles.length && !window.confirm(
      "Esto reemplaza las líneas actuales del detalle por las de la última factura. ¿Continuar?"
    )) {
      return;
    }
    setDetalles(marcarLineaPrincipal(reconstruido));
  };

  // Gate del botón "Copiar detalle": solo si la última factura se puede reconstruir como
  // Concepto Interno (matchea 1 a 1 con las reglas de facturación configuradas hoy para
  // el servicio) — si la última factura era detalle plano/externo, no se ofrece copiarla.
  const puedeCopiarDetalleInterno = !!reconstruirConceptoInterno(historialFacturacion.ultimaFacturaDetalle?.detalles || []);

  const agregarConceptoInterno = () => {
    const concepto = reglasDetalle.find(
      (r) => String(r.metodo_calculo) === String(conceptoDraft.metodo_calculo)
    );
    if (!concepto?.reglas?.length) return;
    const descripcionInterna = conceptoDraft.descripcion_interna.trim();
    if (!descripcionInterna) return;
    const cantidad = conceptoDraft.cantidad;
    const precioBase = conceptoDraft.precio_unitario_base;
    const descuentoBase = conceptoDraft.descuento_interno;
    const groupKey = `manual-${concepto.metodo_calculo}-${Date.now()}`;
    let adicionalAsignado = false;
    const lineas = concepto.reglas.map((regla) => {
      const aplicarAdicional = !adicionalAsignado && Number(regla.orden) === 1;
      if (aplicarAdicional) adicionalAsignado = true;
      return ({
      id_fac_item: Number(regla.id_fac_item),
      id_regla_detalle: regla.id,
      tipo: String(regla.id_fac_item),
      detalle: aplicarAdicional
        ? buildDetalleFiscalPreview(regla.detalle_hippo, descripcionInterna, regla.descripcion_interna)
        : regla.detalle_hippo,
      detalle_hippo_base: regla.detalle_hippo,
      descripcion_interna_base: regla.descripcion_interna || regla.detalle_hippo,
      descripcion_interna: descripcionInterna,
      aplicar_adicional: aplicarAdicional,
      cantidad,
      precio_unitario_base: precioBase,
      precio_unitario: round2(parseDecimal(precioBase) * (Number(regla.porcentaje || 0) / 100)),
      porcentaje_regla: Number(regla.porcentaje || 0),
      orden_regla: Number(regla.orden || 0),
      descuento_interno: round2(parseDecimal(descuentoBase) * (Number(regla.porcentaje || 0) / 100)),
      descuento_interno_input: descuentoBase,
      mes_servicio: conceptoDraft.mes_servicio || formMesNombre,
      year: conceptoDraft.year || String(form.year),
      con_cuota: conceptoDraft.mostrar_cuota === "SI",
      mostrar_cuota: conceptoDraft.mostrar_cuota,
      metodo_calculo: regla.metodo_calculo,
      group_key: groupKey,
      p_3: "",
      });
    });
    setDetalles((prev) => [...prev, ...lineas]);
    setAgregandoConcepto(false);
  };

  const eliminarDetalle = (idx) => {
    if (detalles.length === 1) return;
    setDetalles((prev) => prev.filter((_, i) => i !== idx));
  };

  const eliminarConceptoInterno = (groupKey) => {
    setDetalles((prev) => prev.filter((d) => d.group_key !== groupKey));
  };

  const agregarConceptoVacio = () => {
    const groupKey = `manual-pendiente-${Date.now()}`;
    setDetalles((prev) => [...prev, {
      id_fac_item: "",
      id_regla_detalle: null,
      tipo: "",
      detalle: "",
      detalle_hippo_base: "",
      descripcion_interna_base: "",
      descripcion_interna: "",
      aplicar_adicional: false,
      cantidad: "1",
      precio_unitario_base: "0",
      precio_unitario: 0,
      porcentaje_regla: 0,
      orden_regla: 0,
      descuento_interno: 0,
      descuento_interno_input: "0",
      mes_servicio: formMesNombre,
      year: String(form.year),
      con_cuota: true,
      mostrar_cuota: "SI",
      metodo_calculo: "",
      group_key: groupKey,
      concepto_pendiente: true,
      p_3: "",
    }]);
  };

  const detallesFiscales = detalles
    .map((detalle, index) => ({ detalle, index }))
    .filter(({ detalle }) => !detalle.concepto_pendiente);
  const total = detallesFiscales.reduce(
    (acc, { detalle: d }) => acc + ((parseDecimal(d.cantidad) * parseDecimal(d.precio_unitario)) - parseDecimal(d.descuento_interno)),
    0
  );
  const conceptosInternos = buildInternalGroups(detalles);
  const usaConceptosInternos = conceptosInternos.length > 0;
  const tieneReglasInternasConfiguradas = reglasDetalle.some(
    (concepto) => Array.isArray(concepto?.reglas) && concepto.reglas.length > 0
  );

  const buildRequestBody = () => ({
    id_servicio_cliente        : servicio.cod_servicio_cliente,
    cod_cliente                : servicio.cod_cliente,
    id_legal                   : getLegalIdValue(form.id_legal) || null,
    nit                        : form.nit,
    fac_Nombre                 : form.fac_Nombre,
    tipo_documento_id          : Number(form.codigoTipoDocumentoIdentidad),
    fac_ind                    : individual ? "si" : "no",
    mes                        : form.mes,
    year                       : Number(form.year),
    sucursal                   : form.sucursal,
    codigoMoneda               : Number(form.codigoMoneda),
    venta                      : form.venta,
    tipo                       : "TRS",
    detalles                   : detallesFiscales.map(({ detalle: d }) => {
      const mostrarCuota = cuotaActiva(d) ? "SI" : "NO";
      return {
        id_fac_item       : d.id_fac_item || null,
        id_regla_detalle  : d.id_regla_detalle || null,
        id_detalle_grupo  : d.id_detalle_grupo || null,
        detalle           : stripCuotaDetalle(d.detalle),
        descripcion_interna: d.descripcion_interna || null,
        aplicar_adicional : d.aplicar_adicional !== false,
        cantidad          : parseDecimal(d.cantidad),
        precio_unitario   : parseDecimal(d.precio_unitario),
        precio_unitario_base: d.precio_unitario_base !== undefined ? parseDecimal(d.precio_unitario_base) : undefined,
        descuento_interno : parseDecimal(d.descuento_interno),
        tipo              : String(d.tipo || ""),
        mes_servicio      : d.mes_servicio,
        year              : d.year || String(form.year),
        mostrar_cuota     : mostrarCuota,
        metodo_calculo    : d.metodo_calculo || null,
        group_key         : d.group_key || null,
        p_3               : "",
      };
    }),
  });

  const validateBeforeSubmit = () => {
    if (conceptosInternos.some((grupo) => !grupo.metodo_calculo)) {
      setError("Seleccione el ITEM del concepto interno");
      return false;
    }
    const errConceptos = validarConceptosInternos(detalles);
    if (errConceptos) {
      setError(errConceptos);
      return false;
    }
    const errValidacion = validarDetalles(detallesFiscales.map(({ detalle }) => detalle), form.nit, form.fac_Nombre);
    if (errValidacion) {
      setError(errValidacion);
      return false;
    }
    if (!form.sucursal) {
      setError("La sucursal es requerida");
      return false;
    }
    setError(null);
    return true;
  };

  const handleConceptoInternoTipoChange = (groupKey, nextGroup) => {
    const concepto = reglasDetalle.find(
      (item) => String(item.metodo_calculo) === String(nextGroup.metodo_calculo)
    );
    if (!concepto?.reglas?.length) return;

    let descripcionAsignada = false;
    const nuevasLineas = concepto.reglas.map((regla) => {
      const aplicarDescripcion = !descripcionAsignada && Number(regla.orden) === 1;
      if (aplicarDescripcion) descripcionAsignada = true;
      const porcentaje = Number(regla.porcentaje || 0) / 100;
      return {
        id_fac_item: Number(regla.id_fac_item),
        id_regla_detalle: regla.id,
        tipo: String(regla.id_fac_item),
        detalle: aplicarDescripcion
          ? buildDetalleFiscalPreview(regla.detalle_hippo, nextGroup.descripcion_interna, regla.descripcion_interna)
          : regla.detalle_hippo,
        detalle_hippo_base: regla.detalle_hippo,
        descripcion_interna_base: regla.descripcion_interna || regla.detalle_hippo,
        descripcion_interna: nextGroup.descripcion_interna,
        aplicar_adicional: aplicarDescripcion,
        cantidad: nextGroup.cantidad,
        precio_unitario_base: nextGroup.precio_unitario_base,
        precio_unitario: round2(parseDecimal(nextGroup.precio_unitario_base) * porcentaje),
        porcentaje_regla: Number(regla.porcentaje || 0),
        orden_regla: Number(regla.orden || 0),
        descuento_interno: round2(parseDecimal(nextGroup.descuento_interno) * porcentaje),
        descuento_interno_input: nextGroup.descuento_interno,
        mes_servicio: nextGroup.mes_servicio || formMesNombre,
        year: nextGroup.year || String(form.year),
        con_cuota: (nextGroup.mostrar_cuota || "SI") !== "NO",
        mostrar_cuota: nextGroup.mostrar_cuota || "SI",
        metodo_calculo: regla.metodo_calculo,
        group_key: groupKey,
        p_3: "",
      };
    });

    setDetalles((prev) => {
      const firstIndex = prev.findIndex((d) => d.group_key === groupKey);
      const restantes = prev.filter((d) => d.group_key !== groupKey);
      if (firstIndex < 0) return [...restantes, ...nuevasLineas];
      restantes.splice(firstIndex, 0, ...nuevasLineas);
      return restantes;
    });
  };

  const handlePreview = () => {
    if (!validateBeforeSubmit()) return;
    setPreviewOpen(true);
  };

  const handleSubmit = async () => {
    if (!validateBeforeSubmit()) {
      setPreviewOpen(false);
      return;
    }
    setLoading(true);
    setErrorDetails(null);
    setErrorDetailsOpen(false);
    try {
      const body = buildRequestBody();

      const url = tipo === "fac" ? "/api/facturas/emitir-directo" : "/api/facturas";
      const res = await axios.post(url, body);
      const rawFac = res.data?.data ?? res.data;
      const cod = rawFac?.cod;
      
      onSuccess(cod);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al procesar la operación de facturación");
      setErrorDetails(e?.response?.data?.details?.hippoBody ? { hippoBody: e.response.data.details.hippoBody } : null);
      setErrorDetailsOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          m: 0.5,
          maxHeight: "calc(100% - 8px)",
          "& .MuiDialogTitle-root": { px: 1.5, py: 0.5 },
          "& .MuiDialogContent-root": { px: 1.5, py: 0.5 },
          "& .MuiDialogActions-root": { px: 1.5, py: 0.5 },
        },
      }}
    >
      <DialogTitle sx={{ pb: 0.5, fontSize: "0.9rem", fontWeight: 700, display: "flex", alignItems: "center" }}>
        {tipo === "pre" ? "Pre-factura" : "Factura Directa"} — {servicio?.razon_social || "Servicio"}
        {progreso && (
          <Chip label={`${progreso.actual} / ${progreso.total}`} size="small" color="primary" sx={{ ml: 1.5, height: 18, fontSize: "0.68rem", fontWeight: 700 }} />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent sx={softPanelSx}>
        <Box sx={{ pt: 0.25 }}>
          
          {/* Información comprimida del Contexto */}
          <Box sx={{ mb: 0.75, px: 1, py: 0.4, bgcolor: "rgba(0, 0, 0, 0.02)", borderLeft: "3px solid #008080", borderRadius: "0 2px 2px 0" }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2, fontSize: "0.72rem" }}>
              <strong>#{servicio?.cod_servicio_cliente}</strong> · {servicio?.servicio_nombre} · {servicio?.descripcion} · Base contable: {servicio?.sucursal_facturar || "La Paz"}
            </Typography>
          </Box>

          <AvisoRetiros retiros={retiros} sx={{ mb: 0.75 }} />

          {loadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1.5, p: 3 }}>
              <CircularProgress size={20} color="primary" />
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>Sincronizando estructuras tributarias...</Typography>
            </Box>
          ) : (
            <>
              {/* ── PANEL DE HISTORIAL (AHORA FIJO EN LA PARTE SUPERIOR) ── */}
              <HistorialFacturasPanel
                historial={historialFacturacion}
                onCopiarDetalle={puedeCopiarDetalleInterno ? handleCopiarDetalle : undefined}
              />

              {/* ── FORMULARIO Y DETALLES (ABAJO) ── */}
              <Box sx={{ mt: 1 }}>
                <Seccion titulo="DATOS DE FACTURACIÓN" nota="A nombre de quién se emite" acciones={<>
<Button
                    size="small"
                    variant="outlined"
                    startIcon={<ReceiptLongIcon fontSize="small" />}
                    onClick={() => { setFacturacionDialogInitialData(null); setFacturacionDialogOpen(true); }}
                    disabled={!codClienteResuelto}
                    sx={{ textTransform: "none", fontSize: "0.72rem", minHeight: 28 }}
                  >
                    Datos de facturación
                  </Button>
                  {!tieneLegalServicio && (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      startIcon={<ReceiptLongIcon fontSize="small" />}
                      onClick={() => {
                        // Precarga con lo que ya se está mostrando por defecto (última
                        // factura / informacion_legal del cliente) para no jalar el
                        // formulario vacío — solo falta confirmar/guardar.
                        setFacturacionDialogInitialData({
                          nit: form.nit || "",
                          nombre_facturar: form.fac_Nombre || "",
                          codigo_documento_identidad: Number(form.codigoTipoDocumentoIdentidad) || 5,
                        });
                        setFacturacionDialogOpen(true);
                      }}
                      disabled={!codClienteResuelto}
                      sx={{ textTransform: "none", fontSize: "0.72rem", minHeight: 28 }}
                    >
                      Registrar para este servicio
                    </Button>
                  )}
                </>}>
                {!tieneLegalServicio && (
                  <Alert severity="warning" sx={{ mb: 0.75, py: 0, mt: 0, fontSize: "0.75rem" }}>
                    Informacion de Nit, no registrada, para guardarlo presione el siguiente boton
                  </Alert>
                )}
                {/* Fila 1: datos de facturación (identidad tributaria), las 4 en una
                    sola fila — Nombre se lleva la mayor parte del ancho. */}
                <Grid container spacing={0.75} sx={{ mb: 0 }}>
                  {infoLegales.length > 0 && (
                    <Grid item size={{ xs: 12, md: 3 }}>
                      <TextField select fullWidth size="small" label="Historial de Razón Social / NIT"
                        sx={fieldSx} value={form.id_legal} onChange={(e) => handleSelectLegal(e.target.value)} disabled={loading}>
                        {infoLegales.map((l) => (
                          <MenuItem key={l.legal_key || l.id_legal} value={l.legal_key || l.id_legal} sx={{ fontSize: "0.72rem" }}>
                            {l.nit} - {l.nombre_facturar}{l.origen ? ` (${l.origen})` : ""}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )}

                  <Grid item size={{ xs: 6, md: 1.5 }}>
                    <TextField fullWidth size="small" label="NIT / CI"
                      sx={fieldSx} name="nit" value={form.nit} onChange={handleForm} disabled={loading} />
                  </Grid>

                  <Grid item size={{ xs: 6, md: 2.5 }}>
                    <TextField select fullWidth size="small" label="Tipo Documento"
                      sx={fieldSx} name="codigoTipoDocumentoIdentidad" value={form.codigoTipoDocumentoIdentidad} onChange={handleForm} disabled={loading}>
                      {TIPOS_DOCUMENTO.map((t) => (
                        <MenuItem key={t.codigo} value={t.codigo} sx={{ fontSize: "0.72rem" }}>{t.nombre}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item size={{ xs: 12, md: infoLegales.length > 0 ? 5 : 8 }}>
                    <TextField fullWidth size="small" label="Nombre / Razón Social a Facturar"
                      sx={fieldSx} name="fac_Nombre" value={form.fac_Nombre} onChange={handleForm} disabled={loading} />
                  </Grid>
                </Grid>


                </Seccion>

                <Seccion titulo="PERÍODO Y VENTA" nota="Mes de servicio, sucursal y tipo de venta" acciones={<>
<Button
                    size="small"
                    variant="outlined"
                    startIcon={cargandoServicioPrecio ? <CircularProgress size={14} /> : <PriceChangeIcon fontSize="small" />}
                    onClick={handleAbrirEditarPrecio}
                    disabled={!servicio?.cod_cliente || cargandoServicioPrecio}
                    sx={{ textTransform: "none", fontSize: "0.72rem", minHeight: 28 }}
                  >
                    Editar precio
                  </Button>
                                  </>}>
                {/* Fila 2: período / moneda / sucursal / tipo de venta */}
                <Grid container spacing={0.75} sx={{ mb: 0 }}>
                  <Grid item size={{ xs: 4, md: 2 }}>
  <TextField
    fullWidth
    size="small"
    label="Mes"
    sx={fieldSx}
    name="mes"
    value={form.mes}
    onChange={handleForm}
    disabled={loading}
    inputProps={{ maxLength: 100 }}
  />
</Grid>

                  <Grid item size={{ xs: 3, md: 2 }}>
                    <TextField fullWidth size="small" label="Año" type="text"
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      sx={fieldSx} name="year" value={form.year} onChange={handleForm} disabled={loading} />
                  </Grid>

                  <Grid item size={{ xs: 5, md: 2 }}>
                    <TextField select fullWidth size="small" label="Moneda"
                      sx={fieldSx} name="codigoMoneda" value={form.codigoMoneda} onChange={handleForm} disabled>
                      {MONEDAS.map((m) => <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.72rem" }}>{m.label}</MenuItem>)}
                    </TextField>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 3 }}>
                    <TextField select fullWidth size="small" label="Sucursal"
                      sx={fieldSx} name="sucursal" value={form.sucursal} onChange={handleForm} disabled={loading}>
                      {SUCURSALES.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: "0.72rem" }}>{s}</MenuItem>)}
                    </TextField>
                  </Grid>

                  <Grid item size={{ xs: 6, md: 3 }}>
                    <TextField select fullWidth size="small" label="Tipo Venta"
                      sx={fieldSx} name="venta" value={form.venta} onChange={handleForm} disabled={loading}>
                      <MenuItem value="Alquiler" sx={{ fontSize: "0.72rem" }}>Alquiler</MenuItem>
                      <MenuItem value="Venta" sx={{ fontSize: "0.72rem" }}>Venta</MenuItem>
                      <MenuItem value="Franquicia" sx={{ fontSize: "0.72rem" }}>Franquicia</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
                </Seccion>

                {error && (
                  <Alert
                    severity="error"
                    variant="filled"
                    sx={{ mb: 0.75, py: 0.25, px: 1, fontSize: "0.76rem", fontWeight: 700 }}
                  >
                    <Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <Box sx={{ flex: 1, minWidth: 200 }}>{error}</Box>
                        {servicioPosiblementeInactivo && (
                          <Button
                            size="small"
                            variant="contained"
                            color="inherit"
                            onClick={handleActivarServicio}
                            disabled={activandoServicio}
                            sx={{ textTransform: "none", fontSize: "0.72rem", minHeight: 24, py: 0, color: "error.main" }}
                          >
                            {activandoServicio ? "Activando..." : "Activar servicio"}
                          </Button>
                        )}
                      </Box>
                      {errorDetails?.hippoBody && (
                        <>
                          <Button
                            size="small"
                            color="inherit"
                            onClick={() => setErrorDetailsOpen((v) => !v)}
                            sx={{ mt: 0.25, px: 0, minWidth: 0, fontSize: "0.72rem", textTransform: "none" }}
                          >
                            {errorDetailsOpen ? "Ver menos" : "Ver mas"}
                          </Button>
                          <Collapse in={errorDetailsOpen}>
                            <Box
                              component="pre"
                              sx={{
                                mt: 0.5,
                                mb: 0,
                                p: 1,
                                maxHeight: 260,
                                overflow: "auto",
                                bgcolor: "rgba(0,0,0,0.25)",
                                borderRadius: 1,
                                fontSize: "0.68rem",
                                whiteSpace: "pre-wrap",
                                fontWeight: 500,
                              }}
                            >
                              {formatJson(errorDetails.hippoBody)}
                            </Box>
                          </Collapse>
                        </>
                      )}
                    </Box>
                  </Alert>
                )}

                <Seccion
                  titulo={`CONCEPTOS INTERNOS (${conceptosInternos.length})`}
                  color="#9a3412"
                  nota={(usaConceptosInternos || agregandoConcepto) ? "Recalculan las líneas fiscales para Hippo" : "Opcional: recalcula las líneas fiscales para Hippo"}
                  sinCuerpo
                  acciones={!agregandoConcepto && (
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<AddIcon />}
                      disabled={loading || reglasDetalle.length === 0}
                      sx={{ py: 0, minHeight: 18 }}
                      onClick={() => {
                        setConceptoDraft({
                          metodo_calculo: "",
                          descripcion_interna: "",
                          cantidad: "1",
                          precio_unitario_base: "0",
                          descuento_interno: "0",
                          mostrar_cuota: "SI",
                          mes_servicio: formMesNombre,
                          year: String(form.year),
                        });
                        agregarConceptoVacio();
                      }}
                    >
                      Añadir concepto
                    </Button>
                  )}
                >
                {(usaConceptosInternos || agregandoConcepto) && (
                  <>
                    <Paper variant="outlined" sx={{ ...warningPanelSx, border: 0, borderRadius: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell align="center" sx={{ ...warningHeadCellSx, width: 70 }}>CANT.</TableCell>
                              <TableCell sx={{ ...warningHeadCellSx, width: 210 }}>ITEM</TableCell>
                              <TableCell sx={warningHeadCellSx}>DESCRIPCION INTERNA</TableCell>
                              <TableCell align="right" sx={{ ...warningHeadCellSx, width: 95 }}>P. BASE</TableCell>
                              <TableCell align="right" sx={{ ...warningHeadCellSx, width: 95 }}>DESC.</TableCell>
                              <TableCell sx={{ ...warningHeadCellSx, width: 180 }}>PERIODO</TableCell>
                              <TableCell align="right" sx={{ ...warningHeadCellSx, width: 90 }}>TOTAL</TableCell>
                              <TableCell align="center" sx={{ ...warningHeadCellSx, width: 40 }}>X</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {conceptosInternos.map((grupo) => (
                              <ConceptoInternoRow
                                key={grupo.group_key}
                                grupo={grupo}
                                conceptos={reglasDetalle}
                                onChange={handleConceptoInternoChange}
                                onTipoChange={handleConceptoInternoTipoChange}
                                onDelete={eliminarConceptoInterno}
                                disabled={loading}
                              />
                            ))}
                            {agregandoConcepto && (
                              <TableRow sx={{ "& .MuiTableCell-root": { py: 0.25, px: 0.35 } }}>
                                <Td align="center">
                                  <TextField
                                    size="small"
                                    sx={fieldSx}
                                    inputProps={{ inputMode: "decimal", style: { width: 46, padding: "2px 4px", textAlign: "center" } }}
                                    value={conceptoDraft.cantidad}
                                    onChange={(e) => {
                                      if (allowDecimalInput(e.target.value)) {
                                        setConceptoDraft((prev) => ({ ...prev, cantidad: e.target.value }));
                                      }
                                    }}
                                    disabled={loading}
                                  />
                                </Td>
                                <Td>
                                  <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    sx={fieldSx}
                                    value={conceptoDraft.metodo_calculo}
                                    onChange={(e) => {
                                      const metodoCalculo = e.target.value;
                                      const concepto = reglasDetalle.find(
                                        (item) => String(item.metodo_calculo) === String(metodoCalculo)
                                      );
                                      setConceptoDraft((prev) => ({
                                        ...prev,
                                        metodo_calculo: metodoCalculo,
                                        descripcion_interna: concepto?.descripcion_interna || "",
                                      }));
                                    }}
                                    disabled={loading}
                                  >
                                    <MenuItem value="">Seleccione</MenuItem>
                                    {reglasDetalle.map((regla) => (
                                      <MenuItem key={regla.metodo_calculo} value={regla.metodo_calculo}>
                                        {regla.metodo_calculo}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </Td>
                                <Td>
                                  <TextField
                                    size="small"
                                    fullWidth
                                    placeholder="Descripcion interna"
                                    sx={fieldSx}
                                    value={conceptoDraft.descripcion_interna}
                                    onChange={(e) => setConceptoDraft((prev) => ({ ...prev, descripcion_interna: e.target.value }))}
                                    disabled={loading || !conceptoDraft.metodo_calculo}
                                  />
                                </Td>
                                <Td align="right">
                                  <TextField
                                    size="small"
                                    sx={fieldSx}
                                    inputProps={{ inputMode: "decimal", style: { width: 62, padding: "2px 4px", textAlign: "right" } }}
                                    value={conceptoDraft.precio_unitario_base}
                                    onChange={(e) => {
                                      if (allowDecimalInput(e.target.value)) {
                                        setConceptoDraft((prev) => ({ ...prev, precio_unitario_base: e.target.value }));
                                      }
                                    }}
                                    disabled={loading}
                                  />
                                </Td>
                                <Td align="right">
                                  <TextField
                                    size="small"
                                    sx={fieldSx}
                                    inputProps={{ inputMode: "decimal", style: { width: 58, padding: "2px 4px", textAlign: "right" } }}
                                    value={conceptoDraft.descuento_interno}
                                    onChange={(e) => setConceptoDraft((prev) => ({ ...prev, descuento_interno: e.target.value }))}
                                    disabled={loading}
                                  />
                                </Td>
                                <Td>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
                                    <Checkbox
                                      size="small"
                                      sx={{ p: 0 }}
                                      checked={conceptoDraft.mostrar_cuota === "SI"}
                                      onChange={(e) => setConceptoDraft((prev) => ({ ...prev, mostrar_cuota: e.target.checked ? "SI" : "NO" }))}
                                      disabled={loading}
                                    />
                                    <TextField
                                      select
                                      size="small"
                                      sx={{ ...fieldSx, width: 82 }}
                                      value={conceptoDraft.mes_servicio || formMesNombre}
                                      onChange={(e) => setConceptoDraft((prev) => ({ ...prev, mes_servicio: e.target.value }))}
                                      disabled={loading}
                                    >
                                      {MESES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                    </TextField>
                                    <TextField
                                      size="small"
                                      sx={fieldSx}
                                      inputProps={{ style: { width: 40, padding: "2px 4px" } }}
                                      value={conceptoDraft.year || form.year}
                                      onChange={(e) => setConceptoDraft((prev) => ({ ...prev, year: e.target.value }))}
                                      disabled={loading}
                                    />
                                  </Box>
                                </Td>
                                <Td align="right">
                                  <Typography variant="caption" sx={{ fontWeight: 800 }}>
                                    {((parseDecimal(conceptoDraft.cantidad) * parseDecimal(conceptoDraft.precio_unitario_base)) - parseDecimal(conceptoDraft.descuento_interno)).toFixed(2)}
                                  </Typography>
                                </Td>
                                <Td align="center">
                                  <Box sx={{ display: "flex", justifyContent: "center", gap: 0.15 }}>
                                    <Tooltip title="Guardar">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="success"
                                          onClick={agregarConceptoInterno}
                                          disabled={
                                            loading ||
                                            !conceptoDraft.metodo_calculo ||
                                            !conceptoDraft.descripcion_interna.trim() ||
                                            parseDecimal(conceptoDraft.cantidad) <= 0 ||
                                            parseDecimal(conceptoDraft.precio_unitario_base) <= 0
                                          }
                                          sx={{ p: 0.2 }}
                                        >
                                          <SaveIcon fontSize="inherit" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Cancelar">
                                      <IconButton size="small" color="error" onClick={() => setAgregandoConcepto(false)} disabled={loading} sx={{ p: 0.2 }}>
                                        <CancelIcon fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Td>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </>
                )}

                </Seccion>

                <Seccion
                  titulo={`${usaConceptosInternos ? "LÍNEAS FISCALES PARA HIPPO" : "LÍNEAS DE DETALLE"} (${detallesFiscales.length})`}
                  sinCuerpo
                  acciones={
                    <Button size="small" variant="text" startIcon={<AddIcon  />}
                    onClick={agregarDetalle}
                    disabled={loading || usaConceptosInternos || tieneReglasInternasConfiguradas}
                    sx={{ py: 0, minHeight: 18 }}>
                    Añadir linea
                  </Button>
                  }
                >
                <Paper variant="outlined" sx={{ bgcolor: "background.paper", display: "block", border: 0, borderRadius: 0 }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <Th align="center" sx={{ width: 48 }}>CANT</Th>
                          <Th sx={{ width: 118 }}>ÍTEM</Th>
                          <Th>DESCRIPCIÓN COMERCIAL</Th>
                          <Th sx={{ width: 190 }}>PERIODO CUOTA</Th>
                          <Th align="center" sx={{ width: 64 }}>P. UNIT</Th>
                          <Th align="center" sx={{ width: 62, bgcolor: "#b3392b !important" }}>DESC</Th>
                          <Th align="right" sx={{ width: 80 }}>SUBTOTAL</Th>
                          <Th align="center" sx={{ width: 30 }}>X</Th>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detallesFiscales.map(({ detalle: d, index: idx }) => (
                          <DetalleRow
                            key={idx}
                            d={d}
                            idx={idx}
                            facItems={facItems}
                            onChange={handleDetalleChange}
                            onDelete={eliminarDetalle}
                            disabled={loading || usaConceptosInternos}
                            descriptionDisabled={loading}
                            periodDisabled={loading || usaConceptosInternos}
                          />
                        ))}
                        
                        {/* Totales Fila Compacta */}
                        <TableRow sx={{ ...softPanelSx, height: "26px" }}>
                          <TableCell colSpan={6} align="right" sx={{ py: 0.4, fontWeight: 700, borderTop: "1px solid #008080", fontSize: "0.72rem" }}>
                            TOTAL {form.codigoMoneda === 1 || form.codigoMoneda === "1" ? "$us" : "Bs."}:
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.4, fontWeight: 800, fontSize: "0.78rem", color: "primary.main", borderTop: "1px solid #008080", pr: 0.5 }}>
                            {total.toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ borderTop: "1px solid #008080" }} />
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
                </Seccion>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      
      <Divider />
      <DialogActions sx={{ p: 1 }}>
        <Button onClick={onClose} size="small" variant="outlined" color="inherit" disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handlePreview} size="small" variant="contained" color="primary" disabled={loading || loadingData}>
          Vista previa
        </Button>
      </DialogActions>

      <Dialog open={previewOpen} onClose={() => !loading && setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Vista previa — {tipo === "pre" ? "Pre-factura" : "Factura Directa"}
        </DialogTitle>
        <DialogContent dividers>
          <AvisoRetiros retiros={retiros} sx={{ mb: 1.5 }} />
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={12} md={4}><Typography variant="body2"><strong>Razón social:</strong> {form.fac_Nombre}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography variant="body2"><strong>NIT/CI:</strong> {form.nit}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography variant="body2"><strong>Periodo:</strong> {form.mes} {form.year}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography variant="body2"><strong>Sucursal:</strong> {form.sucursal}</Typography></Grid>
            <Grid item xs={12} md={2}><Typography variant="body2"><strong>Venta:</strong> {form.venta}</Typography></Grid>
          </Grid>

          {conceptosInternos.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Conceptos internos</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 1.5 }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Cant.</TableCell><TableCell>Tipo</TableCell><TableCell>Descripción interna</TableCell><TableCell align="right">P. base</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
                  <TableBody>
                    {conceptosInternos.map((grupo) => (
                      <TableRow key={grupo.group_key}>
                        <TableCell>{grupo.cantidad}</TableCell>
                        <TableCell>{grupo.metodo_calculo || "-"}</TableCell>
                        <TableCell>{grupo.descripcion_interna}</TableCell>
                        <TableCell align="right">{money(grupo.precio_unitario_base)}</TableCell>
                        <TableCell align="right">{money((parseDecimal(grupo.cantidad) * parseDecimal(grupo.precio_unitario_base)) - parseDecimal(grupo.descuento_interno))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Líneas fiscales enviadas</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead><TableRow><TableCell>Cant.</TableCell><TableCell>Ítem</TableCell><TableCell>Descripción comercial</TableCell><TableCell>Periodo</TableCell><TableCell align="right">P. unitario</TableCell><TableCell align="right">Descuento</TableCell><TableCell align="right">Subtotal</TableCell></TableRow></TableHead>
              <TableBody>
                {detallesFiscales.map(({ detalle: d, index: idx }) => (
                  <TableRow key={`${d.id_regla_detalle || d.id_fac_item || "linea"}-${idx}`}>
                    <TableCell>{d.cantidad}</TableCell>
                    <TableCell>{facItems.find((item) => String(item.id) === String(d.id_fac_item || d.tipo))?.item || d.tipo || "-"}</TableCell>
                    <TableCell>{detalleConCuota(d, formMesNombre, form.year)}</TableCell>
                    <TableCell>{cuotaActiva(d) ? `${d.mes_servicio || formMesNombre} ${d.year || form.year}` : "-"}</TableCell>
                    <TableCell align="right">{money(d.precio_unitario)}</TableCell>
                    <TableCell align="right">{money(d.descuento_interno)}</TableCell>
                    <TableCell align="right">{money((parseDecimal(d.cantidad) * parseDecimal(d.precio_unitario)) - parseDecimal(d.descuento_interno))}</TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={6} align="right" sx={{ fontWeight: 800 }}>TOTAL {Number(form.codigoMoneda) === 1 ? "$us" : "Bs."}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900 }}>{money(total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)} color="inherit" variant="outlined" disabled={loading}>Volver a editar</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
            {tipo === "pre" ? "Confirmar y guardar" : "Confirmar y emitir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>

    {facturacionDialogOpen && (
      <InformacionFacturacionDialog
        open={facturacionDialogOpen}
        cliente={{ cod_cliente: codClienteResuelto }}
        servicio={{ cod_servicio_cliente: servicio?.cod_servicio_cliente }}
        initialData={facturacionDialogInitialData}
        onClose={async () => {
          setFacturacionDialogOpen(false);
          setFacturacionDialogInitialData(null);
          cargarInfoLegales(codClienteResuelto);
          // Refresca el aviso de "falta registrar" sin recargar todo el modal, por si
          // se acaba de crear el registro de cliente_servicio_informacion_legal.
          try {
            const { data: raw } = await axios.get("/api/facturas/servicio-detalle", {
              params: { cod_servicio_cliente: servicio.cod_servicio_cliente },
            });
            const data = raw?.data ?? raw;
            setTieneLegalServicio(!!data?.tiene_informacion_legal_servicio);
          } catch {
            // no bloquea el cierre del diálogo si esta verificación falla
          }
        }}
      />
    )}

    {editarPrecioOpen && servicioParaEditarPrecio && (
      <ServicioEditDialog
        open
        servicio={servicioParaEditarPrecio}
        onClose={() => { setEditarPrecioOpen(false); setServicioParaEditarPrecio(null); }}
        onSaved={() => {
          setEditarPrecioOpen(false);
          setServicioParaEditarPrecio(null);
          // Recarga todo el modal para que el detalle/pu se generen con el
          // precio nuevo (mismo criterio que "Activar servicio").
          setLoadingData(true);
          cargarDataModal();
        }}
      />
    )}
    </>
  );
};

export default PrefacturaServicioModal;

// Reexportados para PrefacturaComparativaDialog (vista comparativa masiva), que reutiliza
// esta misma UI/lógica de cálculo en vez de duplicarla.
export {
  Th, Td, money, formatDate, parseDecimal, round2,
  stripCuotaDetalle, cuotaActiva, mesNumeroANombre, getLegalIdValue,
  MESES, mesActual, mesPrevio, yearActual, yearPrevio,
  softPanelSx, compactFieldSx,
  HistorialFacturasPanel, buildInternalGroups, marcarLineaPrincipal,
};
